import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { useSettings } from './SettingsContext';

// Default invoice settings
const defaultInvoiceSettings = {
    invoiceNumbering: {
        prefix: "INV-",
        startNumber: 1001,
        padding: 4,
        includeYear: false,
        resetYearly: false
    },
    paymentMethods: {
        bankgiro: true,
        bankgiroNumber: "",
        plusgiro: false,
        plusgiroNumber: "",
        paypal: false,
        paypalEmail: "",
        swish: false,
        swishNumber: "",
        defaultMethod: "bankgiro"
    },
    deliveryChannels: {
        email: true,
        emailFrom: "",
        emailReplyTo: "",
        emailSubjectTemplate: "Faktura {invoiceNumber} från {companyName}",
        print: false,
        printFormat: "A4",
        eInvoice: false,
        eInvoiceProvider: "",
        sms: false,
        smsFrom: "",
        smsTemplate: "",
        download: true,
        defaultChannel: "email"
    },
    defaultTerms: {
        paymentDays: 30,
        discountDays: 10,
        discountPercentage: 2,
        lateFeePercentage: 8,
        fskattApproved: true
    },
    taxSettings: {
        defaultVatRate: 25,
        includeTaxSummary: true,
        reverseChargeEnabled: false
    },
    template: {
        modern: true,
        classic: true,
        minimal: true,
        defaultTemplate: "classic"
    }
};

const InvoiceSettingsContext = createContext({
    invoiceSettings: defaultInvoiceSettings,
    isLoading: false,
    updateInvoiceSetting: () => { },
    saveInvoiceSettings: () => { }
});
export const InvoiceSettingsProvider = ({ children }) => {
    const { companyId, registerRefreshFunction } = useSettings();
    const [invoiceSettings, setInvoiceSettings] = useState(defaultInvoiceSettings);
    const [isLoading, setIsLoading] = useState(false);
    const [changedSections, setChangedSections] = useState(new Set());


    const fetchInvoiceSettings = async () => {
        if (!companyId) {
            console.warn('InvoiceSettingsContext: No companyId found, skipping fetch.');
            return;
        }

        try {
            setIsLoading(true);
            const url = getApiUrl(`${companyId}/invoice-settings`);
            const { data } = await axios.get(url, { withCredentials: true });

            if (data?.success && Array.isArray(data.settings)) {
                // Initialize with default settings
                const updatedSettings = { ...defaultInvoiceSettings };

                // Process each setting from the array
                data.settings.forEach(setting => {
                    const { setting_key, setting_value } = setting;

                    if (!setting_key || !setting_value) return;

                    try {
                        // Convert setting_value to object if it's a string
                        const value = typeof setting_value === 'string'
                            ? JSON.parse(setting_value)
                            : setting_value;

                        switch (setting_key) {
                            case 'invoice_numbering':
                                updatedSettings.invoiceNumbering = {
                                    prefix: value.prefix || updatedSettings.invoiceNumbering.prefix,
                                    startNumber: value.start_number || value.startNumber || updatedSettings.invoiceNumbering.startNumber,
                                    padding: value.padding || updatedSettings.invoiceNumbering.padding,
                                    includeYear: !!value.include_year,
                                    resetYearly: !!value.reset_yearly
                                };
                                break;

                            case 'payment_methods':
                                updatedSettings.paymentMethods = {
                                    bankgiro: !!value.bankgiro,
                                    bankgiroNumber: value.bankgiro_number || "",
                                    plusgiro: !!value.plusgiro,
                                    plusgiroNumber: value.plusgiro_number || "",
                                    paypal: !!value.paypal,
                                    paypalEmail: value.paypal_email || "",
                                    swish: !!value.swish,
                                    swishNumber: value.swish_number || "",
                                    defaultMethod: value.default_method || "bankgiro"
                                };
                                break;

                            case 'delivery_channels':
                                updatedSettings.deliveryChannels = {
                                    email: !!value.email,
                                    emailFrom: value.email_from || "",
                                    emailReplyTo: value.email_reply_to || "",
                                    emailSubjectTemplate: value.email_subject_template || "Faktura {invoiceNumber} från {companyName}",
                                    print: !!value.print,
                                    printFormat: value.print_format || "A4",
                                    eInvoice: !!value.e_invoice,
                                    eInvoiceProvider: value.e_invoice_provider || "",
                                    sms: !!value.sms,
                                    smsFrom: value.sms_from || "",
                                    smsTemplate: value.sms_template || "",
                                    download: true, // Always enable download option
                                    defaultChannel: value.default_channel || "email"
                                };
                                break;

                            case 'default_terms':
                                updatedSettings.defaultTerms = {
                                    paymentDays: value.payment_days || 30,
                                    discountDays: value.discount_days || 0,
                                    discountPercentage: value.discount_percentage || 0,
                                    lateFeePercentage: value.late_fee_percentage || 8,
                                    fskattApproved: value.fskatt_approved !== undefined ? !!value.fskatt_approved : true,
                                    deliveryTerms: value.delivery_terms || "Fritt vårt lager"
                                };
                                break;

                            case 'tax_settings':
                                updatedSettings.taxSettings = {
                                    defaultVatRate: value.default_vat_rate || 25,
                                    includeTaxSummary: value.include_tax_summary !== undefined ? !!value.include_tax_summary : true,
                                    reverseChargeEnabled: !!value.reverse_charge_enabled
                                };
                                break;

                            case 'template':
                                updatedSettings.template = {
                                    modern: value.modern !== undefined ? !!value.modern : true,
                                    classic: value.classic !== undefined ? !!value.classic : true,
                                    minimal: value.minimal !== undefined ? !!value.minimal : true,
                                    defaultTemplate: value.default_template || "classic"
                                };
                                break;

                            default:
                                console.log(`Unknown setting key: ${setting_key}`);
                        }
                    } catch (error) {
                        console.error(`Error processing setting ${setting_key}:`, error);
                    }
                });

                setInvoiceSettings(updatedSettings);
                setChangedSections(new Set()); // Clear changed sections after fetch
            }
        } catch (error) {
            console.error('Error fetching invoice settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Add this effect
    useEffect(() => {
        if (companyId) {
            fetchInvoiceSettings();
        }
    }, [companyId]);

    // Register this context's refresh function with the parent
    useEffect(() => {
        const unregister = registerRefreshFunction('invoiceSettings', fetchInvoiceSettings);
        return unregister;
    }, [companyId]);


    // Update a section of invoice settings
    const updateInvoiceSetting = (section, sectionData) => {
        setInvoiceSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                ...sectionData
            }
        }));
        // Track which section was changed
        setChangedSections(prev => new Set([...prev, section]));
    };

    // Clear changed sections (useful for manual resets)
    const clearChangedSections = () => {
        setChangedSections(new Set());
    };

    // Save invoice settings to the server

    const saveInvoiceSettings = async (section) => {
        if (!companyId) return;

        try {
            setIsLoading(true);
            const url = getApiUrl(`${companyId}/invoice-settings`);

            // Convert camelCase to snake_case and prepare data for API
            const prepareData = (sectionKey, data) => {
                switch (sectionKey) {
                    case 'invoiceNumbering':
                        return {
                            prefix: data.prefix,
                            padding: data.padding,
                            include_year: data.includeYear,
                            reset_yearly: data.resetYearly,
                            start_number: data.startNumber
                        };

                    case 'paymentMethods':
                        return {
                            bankgiro: data.bankgiro,
                            bankgiro_number: data.bankgiroNumber,
                            plusgiro: data.plusgiro,
                            plusgiro_number: data.plusgiroNumber,
                            paypal: data.paypal,
                            paypal_email: data.paypalEmail,
                            swish: data.swish,
                            swish_number: data.swishNumber,
                            default_method: data.defaultMethod
                        };

                    case 'deliveryChannels':
                        return {
                            email: data.email,
                            email_from: data.emailFrom,
                            email_reply_to: data.emailReplyTo,
                            email_subject_template: data.emailSubjectTemplate,
                            print: data.print,
                            print_format: data.printFormat,
                            e_invoice: data.eInvoice,
                            e_invoice_provider: data.eInvoiceProvider,
                            sms: data.sms,
                            sms_from: data.smsFrom,
                            sms_template: data.smsTemplate,
                            default_channel: data.defaultChannel
                        };

                    case 'defaultTerms':
                        return {
                            payment_days: data.paymentDays,
                            discount_days: data.discountDays,
                            discount_percentage: data.discountPercentage,
                            late_fee_percentage: data.lateFeePercentage,
                            fskatt_approved: data.fskattApproved,
                            delivery_terms: data.deliveryTerms
                        };

                    case 'taxSettings':
                        return {
                            default_vat_rate: data.defaultVatRate,
                            include_tax_summary: data.includeTaxSummary,
                            reverse_charge_enabled: data.reverseChargeEnabled
                        };

                    case 'template':
                        return {
                            modern: data.modern,
                            classic: data.classic,
                            minimal: data.minimal,
                            default_template: data.defaultTemplate
                        };

                    default:
                        return data;
                }
            };

            // If specific section, only update that section
            if (section) {
                const sectionKey = section;
                const apiKey = sectionKey === 'invoiceNumbering' ? 'invoice_numbering' :
                    sectionKey === 'paymentMethods' ? 'payment_methods' :
                        sectionKey === 'deliveryChannels' ? 'delivery_channels' :
                            sectionKey === 'defaultTerms' ? 'default_terms' :
                                sectionKey === 'taxSettings' ? 'tax_settings' :
                                    sectionKey;

                const preparedData = prepareData(sectionKey, invoiceSettings[sectionKey]);

                const { data } = await axios.put(
                    url,
                    {
                        setting_key: apiKey,
                        setting_value: preparedData
                    },
                    { withCredentials: true }
                );

                return data.success;
            } else {
                // Only update changed sections or all if no tracking
                const allSections = [
                    { frontend: 'invoiceNumbering', api: 'invoice_numbering' },
                    { frontend: 'paymentMethods', api: 'payment_methods' },
                    { frontend: 'deliveryChannels', api: 'delivery_channels' },
                    { frontend: 'defaultTerms', api: 'default_terms' },
                    { frontend: 'taxSettings', api: 'tax_settings' },
                    { frontend: 'template', api: 'template' }
                ];

                // Filter to only changed sections if we have tracking
                const sectionsToUpdate = changedSections.size > 0
                    ? allSections.filter(({ frontend }) => changedSections.has(frontend))
                    : allSections;

                console.log('Invoice settings - sections to update:', sectionsToUpdate.map(s => s.frontend));

                const promises = sectionsToUpdate.map(({ frontend, api }) => {
                    const preparedData = prepareData(frontend, invoiceSettings[frontend]);

                    return axios.put(
                        url,
                        {
                            setting_key: api,
                            setting_value: preparedData
                        },
                        { withCredentials: true }
                    );
                });

                const results = await Promise.all(promises);

                // Clear changed sections after successful save
                if (results.every(res => res.data.success)) {
                    setChangedSections(new Set());
                }

                return results.every(res => res.data.success);
            }
        } catch (error) {
            console.error('Error saving invoice settings:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <InvoiceSettingsContext.Provider value={{
            invoiceSettings,
            isLoading,
            updateInvoiceSetting,
            saveInvoiceSettings,
            changedSections,
            clearChangedSections
        }}>
            {children}
        </InvoiceSettingsContext.Provider>
    );
};
export const useInvoiceSettings = () => useContext(InvoiceSettingsContext);
export default InvoiceSettingsContext;
